import {SVG} from '@eCommerce/frontend-legacy-shared-library-native';
import {Divider, Flex, Pressable, Text, VStack, View} from 'native-base';
import React, {useEffect, useRef, useState} from 'react';
import {Animated, LayoutChangeEvent} from 'react-native';
import {Platform} from 'react-native';
import {stripHtmlCode} from 'utils';

import ContainedImage from 'components/ContainedImage';
import OcrmRewardListItemCheck from 'components/OcrmRewardList/OcrmRewardListItem/OcrmRewardListItemCheck';
import TermsPressable from 'components/OcrmRewardList/OcrmRewardListItem/TermsPressable';
import OfferText from 'components/Offers/OfferDetails/OfferDetailsFormattedText';
import OfferDetailsStatus from 'components/Offers/OfferDetails/OfferDetailsStatus';
import useHover from 'hooks/useHover';
import {OfferType} from 'models/Offer';
import {getOfferTypeIcon} from 'utils/getOfferTypeIcon';

export enum OcrmRewardSize {
  Mobile = 'mobile',
  Desktop = 'desktop',
}

interface OcrmRewardStyle {
  borderRadius: string;
  boxSizing: string;
  containerPadding: string;
  contentFlexDirection: string;
  descriptionFontSize: string;
  descriptionLineHeight: string;
  iconSize: string;
  iconTitleSpacing: string;
  imageMaxWidth: string | number;
  innerPadding: string;
  propertyListFontSize: string;
  selectedCheckIconSize: number;
  titleFontSize: string;
}

const OcrmRewardStyles: {[key in OcrmRewardSize]: OcrmRewardStyle} = {
  [OcrmRewardSize.Desktop]: {
    borderRadius: '4px',
    boxSizing: 'border-box',
    containerPadding: '48px',
    contentFlexDirection: 'row',
    descriptionFontSize: '18px',
    descriptionLineHeight: '36px',
    iconSize: '45px',
    iconTitleSpacing: '24px',
    imageMaxWidth: 242,
    innerPadding: '32px',
    propertyListFontSize: '14px',
    selectedCheckIconSize: 38,
    titleFontSize: '30px',
  },
  [OcrmRewardSize.Mobile]: {
    borderRadius: '4px',
    boxSizing: 'border-box',
    containerPadding: '24px',
    contentFlexDirection: 'column',
    descriptionFontSize: '14px',
    descriptionLineHeight: '20px',
    iconSize: '24px',
    iconTitleSpacing: '16px',
    imageMaxWidth: '100%',
    innerPadding: '16px',
    propertyListFontSize: '14px',
    selectedCheckIconSize: 30,
    titleFontSize: '18px',
  },
};

const borderStyle = {
  unselected: {
    color: 'rgba(112,119,132,0.2)',
    type: 'solid',
    width: '1px',
  },
  selected: {
    color: '#343840',
    type: 'solid',
    width: '2px',
  },
};

export interface OcrmRewardListItemProps {
  acceptMessage?: string | undefined;
  description: string;
  id: number;
  imageUrl?: string;
  isAccepted?: boolean;
  margin?: string | number | undefined;
  onSelected?: () => void;
  properties?: string[];
  refinedType: OfferType;
  selected?: boolean;
  size: string;
  terms?: string | undefined;
  termsError?: boolean;
  title: string;
  isDownloadVisible?: boolean;
  setIsDownloadVisible?: (isVisible: boolean) => void;
  onDownloadPress?: () => void;
}

/**
 * Functional component does presentation of ocrm rewards in the details pane.
 */
const OcrmRewardListItem = (props: OcrmRewardListItemProps) => {
  const {
    acceptMessage = undefined,
    description,
    id,
    imageUrl,
    isAccepted = false,
    margin,
    onSelected,
    properties = [],
    refinedType,
    selected = false,
    size,
    terms,
    termsError = false,
    title,
    isDownloadVisible,
    setIsDownloadVisible,
    onDownloadPress,
  } = props;

  const border = selected ? borderStyle.selected : borderStyle.unselected;
  const isDesktopCard = size === OcrmRewardSize.Desktop;
  const style: OcrmRewardStyle =
    OcrmRewardStyles?.[size] || OcrmRewardStyles.mobile;

  const [foldHeight, setFoldHeight] = useState(0);
  const handleLayout = (layoutEvent: LayoutChangeEvent) => {
    const newHeight = layoutEvent.nativeEvent.layout.height;
    if (newHeight > foldHeight) {
      setFoldHeight(newHeight);
    }
  };

  const animFoldHeight = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    Animated.timing(animFoldHeight, {
      duration: 300,
      toValue: selected ? foldHeight || 250 : 0,
      useNativeDriver: false,
    }).start();
  }, [animFoldHeight, foldHeight, selected]);

  const {hovering, handleHoverIn, handleHoverOut} = useHover();

  return (
    <View
      borderWidth={border.width}
      borderStyle={border.type}
      borderColor={border.color}
      borderRadius={style.borderRadius}
      shadow={hovering ? 2 : 0}>
      <Pressable
        accessibilityLabel={
          selected
            ? `Selected ${refinedType} reward ${title}`
            : `Open and select ${refinedType} reward ${title}`
        }
        onHoverIn={handleHoverIn}
        onHoverOut={handleHoverOut}
        testID="ocrm-reward-coupon"
        onPress={onSelected}
        padding={style.containerPadding}>
        {/* Icon and Title */}
        <Flex flexDirection="row" alignItems="flex-start">
          <View height={style.iconSize} width={style.iconSize}>
            <SVG
              alt={`${refinedType} Offer`}
              source={getOfferTypeIcon({
                offerType: refinedType,
                isSelected: true,
              })}
            />
          </View>
          <View minW={style.innerPadding} minH={style.innerPadding} />
          <Text
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            wordBreak={Platform.OS === 'web' ? 'break-word' : undefined}
            flexShrink={1}
            fontSize={style.titleFontSize}
            fontFamily="Montserrat-ExtraBold">
            {title?.toUpperCase()}
          </Text>
        </Flex>
      </Pressable>
      <Animated.View
        style={{
          overflow: 'hidden',
          maxHeight: animFoldHeight,
        }}>
        <View onLayout={handleLayout} paddingX={style.containerPadding}>
          <Divider mb={style.innerPadding} />
          <Flex flexDirection={isDesktopCard ? 'row' : 'column'}>
            {/* Image */}
            <ContainedImage
              imageUrl={imageUrl}
              maxWidth={style.imageMaxWidth}
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              style={
                isDesktopCard
                  ? {
                      marginRight: isDesktopCard ? 32 : 16,
                      width: style.imageMaxWidth,
                    }
                  : {
                      marginBottom: isDesktopCard ? 32 : 16,
                      width: style.imageMaxWidth,
                    }
              }
            />
            {/* Description */}
            <Flex flexShrink={1}>
              <OfferText
                fontSize={style.descriptionFontSize}
                lineHeight={style.descriptionLineHeight}
                fontFamily="OpenSans-Regular"
                text={stripHtmlCode(description)}
              />
              <View minW={style.innerPadding} minH={style.innerPadding} />
              {properties.length === 0 ? null : (
                <VStack space="4px">
                  <Text fontWeight="bold" fontSize={style.propertyListFontSize}>
                    Available at:
                  </Text>
                  {properties.map((p, i) => (
                    <Text
                      key={`${id}-property-${i}`}
                      fontSize={style.propertyListFontSize}>
                      {p}
                    </Text>
                  ))}
                </VStack>
              )}
            </Flex>
          </Flex>

          {!isAccepted && !isDownloadVisible ? null : (
            <>
              <View minW={style.innerPadding} minH={style.innerPadding} />
              <OfferDetailsStatus
                isAccepted={isAccepted}
                postAcceptMessage={acceptMessage}
                mb={0}
                isDownloadVisible={isDownloadVisible}
                setIsDownloadVisible={setIsDownloadVisible}
                onDownloadPress={onDownloadPress}
              />
            </>
          )}

          <TermsPressable
            terms={terms}
            termsError={termsError}
            padding={style.innerPadding}
          />
          <View minH={style.containerPadding} />
        </View>
      </Animated.View>
      {!selected ? null : (
        <OcrmRewardListItemCheck
          color={border.color}
          size={style.selectedCheckIconSize}
        />
      )}
    </View>
  );
};

export default OcrmRewardListItem;
